import { useAppSelector } from 'store/hooks';
import { useCallback, useEffect, useState } from 'react';
import { Breakpoints } from 'apps/core/types/global.types';

export interface WindowSize {
  viewportWidth: number;
  viewportHeight: number;
  mainContentWidth: number;
  isMobile: boolean;
  isTablet: boolean;
  isTabletOrMobile: boolean;
  isMediumOrMobile: boolean;
  isLargeScreen: boolean;
  isMediumScreen: boolean;
}

const useWindowSize = (): WindowSize => {
  const showDesktopSidebar = useAppSelector(({ contextReducer }) => contextReducer.showDesktopSidebar);

  const mainContentWidth = useCallback(() => window.innerWidth, []);

  const [windowSize, setWindowSize] = useState<WindowSize>({
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    mainContentWidth: mainContentWidth(),
    isMobile: mainContentWidth() < Breakpoints.mobile,
    isTablet: mainContentWidth() < Breakpoints.tablet && mainContentWidth() >= Breakpoints.mobile,
    isTabletOrMobile: mainContentWidth() < Breakpoints.tablet,
    isMediumOrMobile: mainContentWidth() < Breakpoints.mediumScreen,
    isLargeScreen: mainContentWidth() >= Breakpoints.largeScreen,
    isMediumScreen:
      mainContentWidth() < Breakpoints.largeScreen && mainContentWidth() >= Breakpoints.mediumScreen,
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        mainContentWidth: mainContentWidth(),
        isMobile: mainContentWidth() < Breakpoints.mobile,
        isTablet: mainContentWidth() < Breakpoints.tablet && mainContentWidth() >= Breakpoints.mobile,
        isTabletOrMobile: mainContentWidth() < Breakpoints.tablet,
        isMediumOrMobile: mainContentWidth() < Breakpoints.mediumScreen,
        isLargeScreen: mainContentWidth() >= Breakpoints.largeScreen,
        isMediumScreen:
          mainContentWidth() < Breakpoints.largeScreen && mainContentWidth() >= Breakpoints.mediumScreen,
      });
    }
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [mainContentWidth, showDesktopSidebar]);

  return windowSize;
};

export default useWindowSize;
